import { zodResolver } from '@hookform/resolvers/zod';
import { CheckCircle2, CircleAlert, Loader2, XCircle } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';

import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import { usePermissions } from '@/features/auth';
import { ApiError } from '@/shared/api/api-error';
import { ConfirmDialog } from '@/shared/components/confirm-dialog';
import { ErrorState } from '@/shared/components/error-state';
import { StatusBadge } from '@/shared/components/status-badge';
import { cn } from '@/shared/lib/utils';

import {
  useCountryReadiness,
  useCurrencies,
  useSetCountryStatus,
  useUpdateCountrySettings,
} from '../api/pricing.queries';
import {
  countrySettingsSchema,
  type CountrySettingsValues,
} from '../schemas/country-settings.schema';
import type { CountryReadiness, MarketCountry } from '../types/pricing.types';

const REASON_CLASS =
  'border-input focus-visible:ring-ring/50 w-full rounded-md border bg-transparent p-2 text-sm outline-none focus-visible:ring-[3px]';
const NONE = 'none';

const STATE_LABEL: Record<CountryReadiness['state'], string> = {
  Active: 'Active',
  Inactive: 'Inactive — ready to activate',
  NotReady: 'Not ready',
};

/**
 * Country settings (BR §7.4): the readiness checklist, activation, and the
 * editable settings. Reading needs pricing.read; changing needs
 * countries.manage. The backend re-checks readiness and the typed name.
 */
export function CountrySettingsPanel({ country }: { country: MarketCountry }) {
  const { can } = usePermissions();
  const canManage = can('countries.manage');

  return (
    <div className="space-y-6">
      <ReadinessSection country={country} canManage={canManage} />
      <Separator />
      <SettingsForm country={country} canManage={canManage} />
    </div>
  );
}

function ReadinessSection({ country, canManage }: { country: MarketCountry; canManage: boolean }) {
  const readiness = useCountryReadiness(country.id);
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [confirmName, setConfirmName] = useState('');
  const setStatus = useSetCountryStatus(country.id);

  if (readiness.isLoading) return <Skeleton className="h-40" />;
  if (readiness.error || !readiness.data) {
    return <ErrorState error={readiness.error} onRetry={() => void readiness.refetch()} />;
  }

  const data = readiness.data;
  const activating = !country.isActive;
  const blocked = activating && !data.isReady;
  const nameMatches = confirmName.trim().toLowerCase() === (country.nameEn ?? '').toLowerCase();

  return (
    <section aria-labelledby="readiness-heading" className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 id="readiness-heading" className="flex items-center gap-2 text-sm font-semibold">
          Readiness
          <StatusBadge
            variant={
              data.state === 'Active'
                ? 'success'
                : data.state === 'NotReady'
                  ? 'warning'
                  : 'neutral'
            }
          >
            {STATE_LABEL[data.state]}
          </StatusBadge>
        </h3>
        {canManage && (
          <Button
            size="sm"
            variant={activating ? 'default' : 'destructive'}
            disabled={blocked}
            title={blocked ? 'Every required check must pass first.' : undefined}
            onClick={() => {
              setReason('');
              setConfirmName('');
              setOpen(true);
            }}
          >
            {activating ? 'Activate country' : 'Deactivate country'}
          </Button>
        )}
      </div>

      <ul className="grid gap-1.5 sm:grid-cols-2">
        {data.checks.map((check) => {
          const Icon = check.passed ? CheckCircle2 : check.required ? XCircle : CircleAlert;
          return (
            <li key={check.code} className="flex gap-2 text-sm">
              <Icon
                className={cn(
                  'mt-0.5 size-4 shrink-0',
                  check.passed
                    ? 'text-status-success'
                    : check.required
                      ? 'text-status-danger'
                      : 'text-status-warning',
                )}
                aria-hidden
              />
              <span>
                <span className="font-medium">{check.label}</span>
                {!check.required && <span className="text-muted-foreground"> (recommended)</span>}
                <span className="sr-only">{check.passed ? ' — passed' : ' — not passed'}</span>
                {check.detail && (
                  <span className="text-muted-foreground block text-xs">{check.detail}</span>
                )}
              </span>
            </li>
          );
        })}
      </ul>

      <ConfirmDialog
        open={open}
        onOpenChange={setOpen}
        title={activating ? `Activate ${country.nameEn}?` : `Deactivate ${country.nameEn}?`}
        description={
          activating
            ? 'Customers in this country can buy again as soon as it is active.'
            : `All customers in ${country.nameEn} will be unable to purchase. Checkouts in progress are re-checked.`
        }
        confirmLabel={activating ? 'Activate' : 'Deactivate'}
        variant={activating ? 'default' : 'destructive'}
        isPending={setStatus.isPending}
        confirmDisabled={reason.trim().length < 3 || (!activating && !nameMatches)}
        onConfirm={() =>
          setStatus.mutate(
            {
              isActive: activating,
              reason: reason.trim(),
              confirmName: activating ? null : confirmName.trim(),
            },
            { onSuccess: () => setOpen(false) },
          )
        }
      >
        <div className="space-y-3">
          {!activating && (
            <div className="space-y-1.5">
              <Label htmlFor="country-confirm-name" className="text-xs">
                Type <span className="font-semibold">{country.nameEn}</span> to confirm
              </Label>
              <Input
                id="country-confirm-name"
                value={confirmName}
                onChange={(event) => setConfirmName(event.target.value)}
                autoComplete="off"
              />
            </div>
          )}
          <div className="space-y-1.5">
            <Label htmlFor="country-status-reason" className="text-xs">
              Reason
            </Label>
            <textarea
              id="country-status-reason"
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              rows={2}
              maxLength={500}
              className={REASON_CLASS}
            />
          </div>
          {setStatus.error instanceof ApiError && (
            <p role="alert" className="text-destructive text-sm">
              {setStatus.error.message}
            </p>
          )}
        </div>
      </ConfirmDialog>
    </section>
  );
}

function SettingsForm({ country, canManage }: { country: MarketCountry; canManage: boolean }) {
  const currencies = useCurrencies();
  const update = useUpdateCountrySettings(country.id);

  const defaults: CountrySettingsValues = {
    nameEn: country.nameEn ?? '',
    nameAr: country.nameAr ?? '',
    currencyCode: country.currencyCode ?? '',
    settlementCurrency: country.settlementCurrency ?? '',
    vat: country.vat,
    siteUrl: country.siteUrl ?? '',
    verificationMethod: country.verificationMethod?.toLowerCase() === 'email' ? 'Email' : 'SMS',
    defaultLocale: country.defaultLocale === 'en' ? 'en' : 'ar',
    reason: '',
  };
  const form = useForm<CountrySettingsValues>({
    resolver: zodResolver(countrySettingsSchema),
    defaultValues: defaults,
  });

  const { error } = update;
  useEffect(() => {
    if (!(error instanceof ApiError) || !error.fieldErrors) return;
    for (const [field, message] of Object.entries(error.fieldErrors)) {
      const key = (field.charAt(0).toLowerCase() + field.slice(1)) as keyof CountrySettingsValues;
      if (key in form.getValues()) form.setError(key, { type: 'server', message });
    }
  }, [error, form]);

  const disabled = !canManage || update.isPending;
  const currencyLocked = country.isActive;
  const currencyOptions = (currencies.data ?? []).filter(
    (c) => c.isActive || c.code === country.currencyCode || c.code === country.settlementCurrency,
  );

  const onSubmit = (values: CountrySettingsValues) =>
    update.mutate(
      {
        nameEn: values.nameEn.trim(),
        nameAr: values.nameAr.trim(),
        currencyCode: values.currencyCode,
        settlementCurrency: values.settlementCurrency === '' ? null : values.settlementCurrency,
        vat: values.vat,
        siteUrl: values.siteUrl.trim() === '' ? null : values.siteUrl.trim(),
        verificationMethod: values.verificationMethod,
        defaultLocale: values.defaultLocale,
        reason: values.reason.trim(),
      },
      { onSuccess: () => form.reset({ ...values, reason: '' }) },
    );

  return (
    <section aria-labelledby="settings-heading" className="space-y-3">
      <div>
        <h3 id="settings-heading" className="text-sm font-semibold">
          Settings
        </h3>
        <p className="text-muted-foreground text-xs">
          Code {country.code ?? '—'} · brand key {country.platformKey ?? '—'} · display currency{' '}
          {country.displayCurrencyMode ?? 'not decided'} (these are set by deployment, not here).
        </p>
      </div>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-5">
          <fieldset disabled={disabled} className="grid gap-5 sm:grid-cols-2">
            <FormField
              control={form.control}
              name="nameEn"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>English name</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="nameAr"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Arabic name</FormLabel>
                  <FormControl>
                    <Input {...field} dir="rtl" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="currencyCode"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Price currency</FormLabel>
                  <Select
                    value={field.value}
                    onValueChange={field.onChange}
                    disabled={disabled || currencyLocked}
                  >
                    <FormControl>
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder="Pick a currency" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {currencyOptions.map((c) => (
                        <SelectItem key={c.code} value={c.code}>
                          {c.code} — {c.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {currencyLocked && (
                    <FormDescription>
                      Deactivate the country to change its currencies.
                    </FormDescription>
                  )}
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="settlementCurrency"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Settlement currency</FormLabel>
                  <Select
                    value={field.value === '' ? NONE : field.value}
                    onValueChange={(value) => field.onChange(value === NONE ? '' : value)}
                    disabled={disabled || currencyLocked}
                  >
                    <FormControl>
                      <SelectTrigger className="w-full">
                        <SelectValue />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value={NONE}>Same as the price currency</SelectItem>
                      {currencyOptions.map((c) => (
                        <SelectItem key={c.code} value={c.code}>
                          {c.code} — {c.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="vat"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>VAT (%)</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      value={Number.isNaN(field.value) ? '' : field.value}
                      onChange={(event) => field.onChange(event.target.valueAsNumber)}
                      type="number"
                      min={0}
                      max={100}
                      step="0.01"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="siteUrl"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Site address</FormLabel>
                  <FormControl>
                    <Input {...field} placeholder="https://" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="verificationMethod"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Verification at sign-up</FormLabel>
                  <Select value={field.value} onValueChange={field.onChange} disabled={disabled}>
                    <FormControl>
                      <SelectTrigger className="w-full">
                        <SelectValue />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="SMS">SMS to the mobile number</SelectItem>
                      <SelectItem value="Email">Email</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="defaultLocale"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Default language</FormLabel>
                  <Select value={field.value} onValueChange={field.onChange} disabled={disabled}>
                    <FormControl>
                      <SelectTrigger className="w-full">
                        <SelectValue />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="ar">Arabic</SelectItem>
                      <SelectItem value="en">English</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
          </fieldset>

          {canManage && (
            <>
              <FormField
                control={form.control}
                name="reason"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Reason</FormLabel>
                    <FormControl>
                      <textarea {...field} rows={2} maxLength={500} className={REASON_CLASS} />
                    </FormControl>
                    <FormDescription>Recorded in the audit log.</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <div className="flex justify-end">
                <Button type="submit" disabled={update.isPending || !form.formState.isDirty}>
                  {update.isPending && <Loader2 className="size-4 animate-spin" aria-hidden />}
                  Save settings
                </Button>
              </div>
            </>
          )}
        </form>
      </Form>
    </section>
  );
}
